import { DataSource, ObjectLiteral, QueryRunner, SelectQueryBuilder } from 'typeorm';
import { DBConnectionType, QueryBuilderOptions, QueryRunnerOptions } from '../types/db/db.types';

/**
 * A utility function to safely create and execute database queries with automatic connection management
 * to either a master (primary) or slave (secondary) database. This function simplifies interaction with
 * TypeORM's query builder, handling connections and cleanup automatically. It is designed for scenarios
 * where you need to query a database and explicitly choose between a master database (for write operations
 * or critical reads) or a slave database (for read-only operations) to optimize performance in a master-slave
 * replication setup.
 *
 * **Important**: Slave databases (`connectionType: DBConnectionType.SECONDARY`) are read-only. Attempting
 * to perform write operations (e.g., INSERT, UPDATE, DELETE) on a slave database will result in an error.
 * Use the master database (`connectionType: DBConnectionType.PRIMARY`) for any operations that modify data.
 * Use the slave database for read-only operations to offload read traffic from the master database, improving
 * performance in high-traffic applications.
 *
 * **When to Read from Master Instead of Slave**:
 * - **Reading Recently Created or Updated Resources**: Read from the master database when you have just created
 *   or updated a resource (e.g., a new user or an updated order) and need to read that same resource immediately
 *   afterward, as slave databases may have replication lag and might not yet reflect the latest changes.
 * - **Critical Data Consistency**: Read from the master database when you need the most up-to-date data, as slave
 *   databases may have replication lag (e.g., checking account balances after a transaction).
 * - **Transactional Reads**: If your read operation is part of a transaction that includes writes, use the master
 *   database to ensure consistency within the transaction.
 * - **Low-Latency Requirements**: If the slave database is geographically distant and latency is a concern, reading
 *   from the master may be faster in some cases.
 *
 * **Callback Return Value**:
 * The `operation` callback receives a `SelectQueryBuilder<T>` and must explicitly return the result of the query
 * (e.g., a single entity, an array of entities, or a query execution result). This return value becomes the resolved
 * value of the `usingAioQueryBuilder` function, wrapped in a `Promise`. Ensure the callback includes a `return`
 * statement to avoid returning `undefined`. The return type (`R`) can be specified via TypeScript generics to match
 * the expected output (e.g., `User[]` for a list of users, `InsertResult` for an insert operation).
 *
 * @template T - The type of database entity you're working with (e.g., User, Product).
 * @template R - The type of result expected from the query operation (e.g., T, T[], InsertResult).
 *
 * @param {QueryBuilderOptions<T>} options - Configuration options for the query, which can be one of:
 *   - `RepositoryQueryBuilderOptions<T>`: Use a TypeORM repository with an optional alias.
 *   - `EntityQueryBuilderOptions<T>`: Use an entity class with an optional alias.
 *   - `RawQueryBuilderOptions`: Use for raw SQL queries without a specific entity or repository.
 * @param {DataSource} [options.dataSource] - The TypeORM DataSource for the database connection.
 * @param {Repository<T>} [options.repository] - The TypeORM repository for the entity (mutually exclusive with `entity`).
 * @param {EntityTarget<T>} [options.entity] - The entity class for the query (mutually exclusive with `repository`).
 * @param {string} [options.alias] - The alias for the entity in the query (e.g., 'user' for User entity).
 * @param {QueryRunner} [options.queryRunner] - An optional external query runner to use instead of creating a new one.
 * @param {DBConnectionType} [options.connectionType] - Specifies the database connection:
 *   - `DBConnectionType.PRIMARY`: Use the master database (for writes and critical reads).
 *   - `DBConnectionType.SECONDARY`: Use the slave database (for reads only).
 * @param {(queryBuilder: SelectQueryBuilder<T>) => Promise<R> | R} operation - The query logic to execute using the query builder.
 *   Must return the desired result (e.g., `qb.getMany()`, `qb.execute()`).
 *
 * @returns {Promise<ReturnType<typeof operation>>} - The result of the query operation, as returned by the `operation` callback.
 *
 * @throws {Error} - If neither `dataSource` nor `repository` is provided, or if the query runner is not available.
 *
 * @example
 * // Example 1: Read from slave database (read-only)
 * const users = await usingAioQueryBuilder<User[], User>(
 *   {
 *     repository: userRepository,
 *     alias: 'user',
 *     connectionType: DBConnectionType.SECONDARY
 *   },
 *   async (qb) => {
 *     return qb.where('user.age > :age', { age: 18 }).getMany(); // Explicit return
 *   }
 * );
 * // `users` is User[]
 *
 * @example
 * // Example 2: Write to master database
 * const insertResult = await usingAioQueryBuilder<InsertResult, Product>(
 *   {
 *     dataSource: myDataSource,
 *     entity: Product,
 *     alias: 'product',
 *     connectionType: DBConnectionType.PRIMARY
 *   },
 *   async (qb) => {
 *     return qb.insert().into(Product).values({ name: 'New Product', price: 100 }).execute(); // Explicit return
 *   }
 * );
 * // `insertResult` is InsertResult
 *
 * @example
 * // Example 3: Read from master database after creating a resource
 * const newUser = await usingAioQueryBuilder<User, User>(
 *   {
 *     repository: userRepository,
 *     alias: 'user',
 *     connectionType: DBConnectionType.PRIMARY
 *   },
 *   async (qb) => {
 *     await qb.insert().into(User).values({ name: 'John Doe', age: 30 }).execute();
 *     return qb.where('user.name = :name', { name: 'John Doe' }).getOne(); // Explicit return
 *   }
 * );
 * // `newUser` is User
 *
 * @remarks
 * - Use this function in applications with master-slave replication to direct read queries to slave databases
 *   and write queries or critical reads (e.g., after creating/updating a resource) to the master database.
 * - Ensure `connectionType` is set to `DBConnectionType.PRIMARY` for any write operations or when reading
 *   recently created/updated resources.
 * - Use `connectionType: DBConnectionType.SECONDARY` for read-heavy, non-critical operations to leverage
 *   slave databases.
 * - The `operation` callback must explicitly return the query result to avoid unexpected `undefined` results.
 * - The `operation` callback should include proper error handling for database-specific errors.
 */
async function usingAioQueryBuilder<T extends ObjectLiteral = any, R = any>(
  options: QueryBuilderOptions<T>,
  operation: (queryBuilder: SelectQueryBuilder<T>) => Promise<R> | R
): Promise<ReturnType<typeof operation>> {
  const { queryRunner: externalQueryRunner, connectionType } = options;
  let { dataSource } = options;
  if (!dataSource && options.repository) dataSource = options.repository?.manager.connection;
  if (!dataSource) {
    throw new Error('DataSource is not available, please pass it in options or provide a repository');
  }
  const shouldManageQueryRunner = !externalQueryRunner && connectionType;
  let queryRunner: QueryRunner | undefined = externalQueryRunner;

  try {
    if (shouldManageQueryRunner) {
      queryRunner = dataSource?.createQueryRunner(connectionType === DBConnectionType.SECONDARY ? 'slave' : 'master');
    }

    let queryBuilder: SelectQueryBuilder<T>;
    if ('repository' in options && options.repository) {
      queryBuilder = options.repository.createQueryBuilder(options.alias, queryRunner);
    } else if ('entity' in options && options.entity) {
      queryBuilder = dataSource?.createQueryBuilder(options.entity, options?.alias ?? '', queryRunner);
    } else {
      // Raw query builder mode
      queryBuilder = dataSource?.createQueryBuilder(queryRunner) as SelectQueryBuilder<T>;
    }

    const result = await operation(queryBuilder);

    return result;
  } finally {
    if (shouldManageQueryRunner && queryRunner) {
      await queryRunner.release();
    }
  }
}

/**
 * A utility function to safely work with TypeORM query runners for direct database operations,
 * allowing connections to either a master (primary) or slave (secondary) database. This function
 * is designed for low-level database operations, such as running raw SQL queries or managing transactions,
 * with automatic connection setup and cleanup. It is particularly useful in master-slave replication setups
 * where you need to direct read operations to a slave database and write operations or critical reads to a
 * master database.
 *
 * **Important**: Slave databases (`connectionType: DBConnectionType.SECONDARY`) are read-only. Attempting
 * to perform write operations (e.g., INSERT, UPDATE, DELETE) on a slave database will result in an error.
 * Use the master database (`connectionType: DBConnectionType.PRIMARY`) for any operations that modify data.
 * Use the slave database for read-only operations to improve performance by offloading read traffic from the
 * master database.
 *
 * **When to Read from Master Instead of Slave**:
 * - **Reading Recently Created or Updated Resources**: Read from the master database when you have just created
 *   or updated a resource (e.g., a new user or an updated account balance) and need to read that same resource
 *   immediately afterward, as slave databases may have replication lag and might not yet reflect the latest changes.
 * - **Critical Data Consistency**: Read from the master database when you need the most up-to-date data, as slave
 *   databases may have replication lag (e.g., checking account balances after a transaction).
 * - **Transactional Reads**: If your read operation is part of a transaction that includes writes, use the master
 *   database to ensure consistency within the transaction.
 * - **Low-Latency Requirements**: If the slave database is geographically distant and latency is a concern, reading
 *   from the master may be faster in some cases.
 *
 * **Callback Return Value**:
 * The `operation` callback receives a `QueryRunner` and must explicitly return a `Promise` with the result of the
 * database operation (e.g., raw query results, a saved entity, or a custom value). This return value becomes the
 * resolved value of the `usingAioQueryRunner` function. Ensure the callback includes a `return` statement to avoid
 * returning `undefined`. The return type (`R`) can be specified via TypeScript generics to match the expected output
 * (e.g., `number` for a count query, `User` for a saved entity).
 *
 * @template R - The type of result expected from the operation (e.g., number, object, User).
 *
 * @param {QueryRunnerOptions & { schema?: string }} options - Configuration options for the query runner.
 * @param {DataSource} [options.dataSource] - The TypeORM DataSource for the database connection.
 * @param {Repository<any>} [options.repository] - The TypeORM repository (alternative to `dataSource`).
 * @param {QueryRunner} [options.queryRunner] - An optional external query runner to use instead of creating a new one.
 * @param {DBConnectionType} [options.connectionType] - Specifies the database connection:
 *   - `DBConnectionType.PRIMARY`: Use the master database (for writes and critical reads).
 *   - `DBConnectionType.SECONDARY`: Use the slave database (for reads only).
 * @param {string} [options.schema] - Optional schema to set as the search path for the query runner.
 * @param {(qr: QueryRunner) => Promise<R>} operation - The database operation to execute using the query runner.
 *   Must return a `Promise` with the desired result (e.g., `qr.query(...)`, `qr.manager.save(...)`).
 *
 * @returns {Promise<R>} - The result of the database operation, as returned by the `operation` callback.
 *
 * @throws {Error} - If neither `dataSource` nor `repository` is provided, or if the query runner is not available.
 *
 * @example
 * // Example 1: Run raw SQL on slave database (read-only)
 * const userCount = await usingAioQueryRunner<number>(
 *   {
 *     dataSource: myDataSource,
 *     connectionType: DBConnectionType.SECONDARY,
 *     schema: 'public'
 *   },
 *   async (qr) => {
 *     const result = await qr.query('SELECT COUNT(*) FROM public.users WHERE active = $1', [true]);
 *     return Number(result[0].count); // Explicit return
 *   }
 * );
 * // `userCount` is number
 *
 * @example
 * // Example 2: Transaction on master database
 * const updatedUser = await usingAioQueryRunner<User>(
 *   {
 *     repository: userRepository,
 *     connectionType: DBConnectionType.PRIMARY
 *   },
 *   async (qr) => {
 *     await qr.startTransaction();
 *     try {
 *       const user = await qr.manager.findOne(User, { where: { id: 1 } });
 *       user.name = 'Updated Name';
 *       await qr.manager.save(user);
 *       await qr.commitTransaction();
 *       return user; // Explicit return
 *     } catch (error) {
 *       await qr.rollbackTransaction();
 *       throw error;
 *     }
 *   }
 * );
 * // `updatedUser` is User
 *
 * @example
 * // Example 3: Read from master database after updating a resource
 * const updatedBalance = await usingAioQueryRunner<number>(
 *   {
 *     dataSource: myDataSource,
 *     connectionType: DBConnectionType.PRIMARY
 *   },
 *   async (qr) => {
 *     await qr.query('UPDATE tenant.accounts SET balance = balance + $1 WHERE userId = $2', [100, userId]);
 *     const result = await qr.query('SELECT balance FROM tenant.accounts WHERE userId = $1', [userId]);
 *     return Number(result[0].balance); // Explicit return
 *   }
 * );
 * // `updatedBalance` is number
 *
 * @remarks
 * - Use this function for low-level database operations or raw SQL queries in a master-slave replication setup.
 * - Choose `connectionType: DBConnectionType.SECONDARY` for read-only operations to leverage slave databases.
 * - Use `connectionType: DBConnectionType.PRIMARY` for any operation that modifies data or requires reading
 *   recently created/updated resources.
 * - If a `schema` is provided, it will be set as the search path for the query runner, but for raw queries, always
 *   include the schema name explicitly (e.g., `tenant.table`) to ensure correct tenant isolation.
 * - Ensure the `operation` callback explicitly returns a `Promise` with the desired result to avoid unexpected `undefined` results.
 * - Ensure proper transaction management (start, commit, rollback) in your `operation` callback if needed.
 */
async function usingAioQueryRunner<R = any>(
  options: QueryRunnerOptions & { schema?: string },
  operation: (qr: QueryRunner) => Promise<R>
): Promise<R> {
  const { queryRunner: externalQueryRunner, connectionType, schema } = options;
  let { dataSource } = options;
  if (!dataSource && options.repository) dataSource = options.repository?.manager.connection;
  if (!dataSource && !externalQueryRunner) {
    throw new Error('DataSource is not available, please pass it in options or provide a repository');
  }

  const shouldManageQueryRunner = !externalQueryRunner;
  let queryRunner: QueryRunner | undefined = externalQueryRunner;

  try {
    if (shouldManageQueryRunner) {
      queryRunner = dataSource?.createQueryRunner(connectionType === DBConnectionType.SECONDARY ? 'slave' : 'master');
      await queryRunner?.connect();

      if (schema) {
        await queryRunner?.query(`SET search_path TO ${schema}`);
      }
    }
    if (!queryRunner) {
      throw new Error('Query runner not available');
    }
    const result = await operation(queryRunner);
    return result;
  } finally {
    if (shouldManageQueryRunner && queryRunner && queryRunner.isReleased === false) {
      await queryRunner.release();
    }
  }
}

/**
 * Sets the database search path to a specific schema for the master (primary), slave (secondary),
 * or both database connections. The search path determines which schema the database looks in
 * for tables and other database objects when a schema is not explicitly specified in queries.
 * This function is useful in master-slave replication setups where you need to work with a specific
 * schema for raw queries in multi-tenant applications.
 *
 * **Important**: Slave databases (`connectionType: DBConnectionType.SECONDARY`) are read-only.
 * This function only sets the schema and does not enable write operations on slave databases.
 * Use the master database (`connectionType: DBConnectionType.PRIMARY`) for write operations.
 * Use the slave database for read-only operations to optimize performance.
 *
 * **When to Read from Master Instead of Slave**:
 * - **Reading Recently Created or Updated Resources**: Read from the master database when you have just created
 *   or updated a resource (e.g., a new user or an updated account balance) and need to read that same resource
 *   immediately afterward, as slave databases may have replication lag and might not yet reflect the latest changes.
 * - **Critical Data Consistency**: Read from the master database when you need the most up-to-date data, as slave
 *   databases may have replication lag (e.g., checking account balances after a transaction).
 * - **Transactional Reads**: If your read operation is part of a transaction that includes writes, use the master
 *   database to ensure consistency within the transaction.
 * - **Low-Latency Requirements**: If the slave database is geographically distant and latency is a concern, reading
 *   from the master may be faster in some cases.
 * - **No Slave Available**: If your database setup does not have a slave database or it is temporarily unavailable,
 *   use the master database for all operations.
 *
 * **Connection Pool and Schema Considerations**:
 * The `DataSource` uses a connection pool with multiple connections. When setting the search path using a raw query
 * (e.g., `SET search_path TO tenant`), it affects only the specific connection used for that query, not all connections
 * in the pool. Subsequent queries may use the same connection (and thus follow the set search path) or a different one
 * (which will not have the search path set). This function works in typical scenarios, such as when an endpoint is called
 * and a middleware sets the search path to the correct tenant (obtained from the request payload) using a raw query. In
 * low-concurrency environments like testing, the same connection is often reused, ensuring the search path is applied.
 * However, in high-concurrency scenarios (e.g., parallel queries with `Promise.all`), different connections may be used,
 * and those connections will not follow the previously set search path, potentially leading to incorrect schema access.
 *
 * **Schema in Raw Queries**:
 * For raw queries, always include the schema name explicitly (e.g., `SELECT * FROM tenant.users`) to ensure the correct
 * tenant is targeted, regardless of the search path set by this function. Repository-based operations (e.g., `find`,
 * `update`) automatically target the correct tenant schema due to the `DataSource` configuration (e.g., via a
 * `getTenantDataSource` function), making the search path irrelevant for these operations. Explicitly specifying the
 * schema in raw queries ensures tenant isolation and avoids reliance on the connection-specific search path.
 *
 * @param {DataSource} dataSource - The TypeORM DataSource for the database connection.
 * @param {string} schema - The schema name to set as the search path (e.g., 'public', 'tenant1').
 * @param {DBConnectionType | 'both'} connectionType - Specifies which database connections to configure:
 *   - `DBConnectionType.PRIMARY`: Sets the schema for the master database (used for writes and critical reads).
 *   - `DBConnectionType.SECONDARY`: Sets the schema for the slave database (used for reads only).
 *   - `'both'`: Sets the schema for both master and slave databases.
 *
 * @returns {Promise<void>} - Resolves when the search path is set.
 *
 * @throws {Error} - If there's an issue setting the search path or connecting to the database.
 *
 * @example
 * // Example 1: Set search path for both master and slave databases
 * await setSearchPath(myDataSource, 'tenant1', 'both');
 *
 * @example
 * // Example 2: Set search path for master database only
 * await setSearchPath(myDataSource, 'public', DBConnectionType.PRIMARY);
 *
 * @example
 * // Example 3: Set search path for slave database and use explicit schema in raw query
 * await setSearchPath(myDataSource, 'tenant1', DBConnectionType.SECONDARY);
 * const users = await myDataSource.query('SELECT * FROM tenant1.users WHERE active = $1', [true]);
 *
 * @remarks
 * - Use this function to set the search path in a multi-tenant database setup, particularly for raw queries in
 *   master-slave replication scenarios.
 * - Always include the schema name in raw queries to ensure correct tenant isolation, as repository operations
 *   (e.g., `find`, `update`) are handled by the `DataSource` configuration and do not rely on the search path.
 * - Be cautious in high-concurrency environments; consider setting the search path on each connection acquisition
 *   or using explicit schema names in raw queries to avoid inconsistencies.
 */
async function setSearchPath(
  dataSource: DataSource,
  schema: string,
  connectionType: DBConnectionType | 'both'
): Promise<void> {
  if (connectionType === DBConnectionType.PRIMARY || connectionType === 'both') {
    await dataSource.query(`SET search_path TO ${schema}`);
    if (connectionType === DBConnectionType.PRIMARY) return;
  }

  let replicaQr: QueryRunner | null = null;
  try {
    replicaQr = dataSource.createQueryRunner('slave');
    await replicaQr.connect();
    await dataSource.query(`SET search_path TO ${schema}`, undefined, replicaQr);
    await replicaQr.release();
  } catch (error) {
    if (replicaQr && !replicaQr.isReleased) await replicaQr.release();
    throw error;
  }
}

export { usingAioQueryBuilder, usingAioQueryRunner, setSearchPath };
